import React, { useState, useEffect, useRef } from 'react';
import './LiveStream.css';

const LiveStream = ({ user, visible = false, onClose }) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [streamUrl, setStreamUrl] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [facingMode, setFacingMode] = useState('environment'); // rear camera by default — not a selfie
  const [actualFacingMode, setActualFacingMode] = useState(null); // what the camera actually gave us, not what we asked for
  const videoRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const wakeLockRef = useRef(null);
  const credsRef = useRef(null);       // { whipUrl, viewUrl, authHeader } from the backend
  const resourceUrlRef = useRef(null); // WHIP resource URL (Location header) — DELETE target on stop

  const streamId = user.username;
  const RESOLUTION = { width: 1280, height: 720 }; // 720p30 — 1080p + a 4Mbps ceiling outran real-world mobile uplink and showed as decode corruption, not just blur

  // Available cameras (desktop: pick one; mobile: front/back toggle below)
  useEffect(() => {
    const initializeCameras = async () => {
      try {
        try { if (sessionStorage.getItem('pm_menuboard_admin') === '1') return; } catch (e) {}
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cameras = devices.filter(device => device.kind === 'videoinput');
        setAvailableCameras(cameras);
        if (cameras.length > 0) setSelectedCamera(cameras[0].deviceId);
      } catch (err) {
        console.error('Failed to enumerate cameras:', err);
      }
    };
    initializeCameras();
    return () => { stopStreaming(); };
  }, []);

  useEffect(() => {
    const acquireWakeLock = async () => {
      try {
        if (!isStreaming || !('wakeLock' in navigator) || wakeLockRef.current) return;
        const lock = await navigator.wakeLock.request('screen');
        wakeLockRef.current = lock;
        lock.addEventListener('release', () => { wakeLockRef.current = null; });
      } catch (e) { /* non-fatal */ }
    };
    const releaseWakeLock = async () => {
      try {
        if (wakeLockRef.current) { await wakeLockRef.current.release(); wakeLockRef.current = null; }
      } catch (e) { /* non-fatal */ }
    };
    if (isStreaming) acquireWakeLock(); else releaseWakeLock();
    return () => { releaseWakeLock(); };
  }, [isStreaming]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && isStreaming && 'wakeLock' in navigator && !wakeLockRef.current) {
        navigator.wakeLock.request('screen')
          .then(lock => { wakeLockRef.current = lock; lock.addEventListener('release', () => { wakeLockRef.current = null; }); })
          .catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isStreaming]);

  // Mint this user's stream credentials from the MAESTRO backend. The backend
  // is the one that knows (and keeps secret) the MediaMTX password — the
  // frontend only ever sees a short-lived value handed to it right before
  // publishing, scoped to this request.
  const authToken = () => {
    try { return sessionStorage.getItem('pm_tab_token') || localStorage.getItem('token'); }
    catch (e) { return localStorage.getItem('token'); }
  };

  const fetchCredentials = async () => {
    let res;
    try {
      res = await fetch('/api/livestream/credentials', {
        headers: { 'Authorization': `Bearer ${authToken()}` }
      });
    } catch (e) {
      throw new Error(`Could not reach the app server (/api/livestream/credentials): ${e.message}`);
    }
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`Could not get stream credentials (HTTP ${res.status}) ${t}`);
    }
    return res.json(); // { whipUrl, viewUrl, authHeader }
  };

  const createPeerConnection = (stream) => {
    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    });

    stream.getTracks().forEach(track => pc.addTrack(track, stream));

    // MediaMTX's HLS output (what the copied link / signage boards play) only
    // muxes H264. Browsers default the video sender to VP8, which gets
    // silently dropped from the HLS output — audio-only, no picture. Force
    // H264 before the offer goes out.
    try {
      const videoTransceiver = pc.getTransceivers().find(t => t.sender && t.sender.track && t.sender.track.kind === 'video');
      if (videoTransceiver && videoTransceiver.setCodecPreferences) {
        const caps = RTCRtpSender.getCapabilities('video');
        const h264 = caps.codecs.filter(c => c.mimeType.toLowerCase() === 'video/h264');
        if (h264.length) videoTransceiver.setCodecPreferences(h264);
      }
    } catch (e) { /* falls back to default codec */ }

    // Without an explicit bitrate, the browser's own bandwidth ramp-up starts
    // low and climbs slowly — on a short stream it may never reach a bitrate
    // that actually looks good, which reads as "weak quality". But this is a
    // CEILING, not a forced floor — set it too high for the real uplink and
    // congestion control pushes more than the network carries, which comes
    // out as packet loss and corrupted decode (seen 2026-10-26 at 1080p +
    // 4Mbps: tiled/smeared blocks, not just blur — a genuinely worse result
    // than the unset default). 1.8Mbps is a realistic ceiling for 720p on a
    // mobile uplink; still well above what the default ramp would reach on a
    // short session, without outrunning the network. degradationPreference
    // 'maintain-framerate' still applies on top of that: if even this gets
    // tight, resolution/sharpness gives first, not frames.
    try {
      const videoSender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
      if (videoSender) {
        const params = videoSender.getParameters();
        if (!params.encodings || !params.encodings.length) params.encodings = [{}];
        params.encodings[0].maxBitrate = 1_800_000; // ~1.8 Mbps ceiling for 720p30
        params.degradationPreference = 'maintain-framerate';
        videoSender.setParameters(params).catch(() => {});
      }
    } catch (e) { /* non-fatal — falls back to browser defaults */ }

    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
        setError('Connection lost.');
        setIsStreaming(false); // the LIVE badge must not keep pulsing over a dead connection
      }
    };

    return pc;
  };

  const getMediaConstraints = () => {
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const constraints = {
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      video: { width: { ideal: RESOLUTION.width }, height: { ideal: RESOLUTION.height }, frameRate: { ideal: 30, max: 30 } }
    };
    if (isMobile) {
      constraints.video.facingMode = facingMode;
    } else if (selectedCamera) {
      constraints.video.deviceId = { exact: selectedCamera };
    }
    return constraints;
  };

  // The browser doesn't always honor the facingMode we asked for (some
  // devices silently fall back to whatever camera they feel like) — read
  // back what we actually got instead of trusting our own request, so the
  // mirror-or-not decision matches the real camera, not our assumption.
  const detectFacing = (stream) => {
    try {
      const track = stream.getVideoTracks()[0];
      const settings = track.getSettings ? track.getSettings() : {};
      if (settings.facingMode === 'user' || settings.facingMode === 'environment') {
        return settings.facingMode;
      }
      const label = (track.label || '').toLowerCase();
      if (label.includes('front')) return 'user';
      if (label.includes('back') || label.includes('rear')) return 'environment';
    } catch (e) { /* unknown — don't mirror */ }
    return null;
  };

  const startStreaming = async () => {
    try {
      setError(null);
      setConnecting(true);

      const constraints = getMediaConstraints();
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStreamRef.current = stream;
      setActualFacingMode(detectFacing(stream));
      if (videoRef.current) videoRef.current.srcObject = stream;

      const creds = await fetchCredentials();
      credsRef.current = creds;

      const pc = createPeerConnection(stream);
      peerConnectionRef.current = pc;

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // WHIP is a single-shot offer/answer — wait for ICE gathering to finish
      // so the SDP we POST already carries our candidates (no trickle).
      await new Promise(resolve => {
        if (pc.iceGatheringState === 'complete') return resolve();
        const check = () => {
          if (pc.iceGatheringState === 'complete') { pc.removeEventListener('icegatheringstatechange', check); resolve(); }
        };
        pc.addEventListener('icegatheringstatechange', check);
        setTimeout(resolve, 3000);
      });

      let res;
      try {
        res = await fetch(creds.whipUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/sdp', 'Authorization': creds.authHeader },
          body: pc.localDescription.sdp
        });
      } catch (e) {
        throw new Error(`Could not reach the broadcast server (${creds.whipUrl}): ${e.message}`);
      }
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(`WHIP publish failed: HTTP ${res.status} ${t}`);
      }

      let resourceUrl = res.headers.get('Location');
      if (resourceUrl && !resourceUrl.startsWith('http')) resourceUrl = new URL(resourceUrl, creds.whipUrl).href;
      resourceUrlRef.current = resourceUrl;

      const answerSdp = await res.text();
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

      setIsStreaming(true);
      setConnecting(false);
      setStreamUrl(creds.viewUrl);
      setError(null);

    } catch (err) {
      console.error('❌ Camera/Streaming error:', err);
      if (err.name === 'NotAllowedError') setError('Camera permission denied. Please allow camera access.');
      else if (err.name === 'NotFoundError') setError('No camera found.');
      else if (err.name === 'NotReadableError') setError('Camera is already in use by another app.');
      else setError(err.message || 'Failed to start streaming. Please try again.');

      setConnecting(false);
      if (peerConnectionRef.current) { peerConnectionRef.current.close(); peerConnectionRef.current = null; }
      if (localStreamRef.current) { localStreamRef.current.getTracks().forEach(t => t.stop()); localStreamRef.current = null; }
      if (videoRef.current) videoRef.current.srcObject = null;
    }
  };

  const switchCamera = async () => {
    if (!isStreaming) return;
    try {
      if (localStreamRef.current) localStreamRef.current.getTracks().forEach(t => t.stop());

      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (isMobile) {
        setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
      } else {
        const currentIndex = availableCameras.findIndex(c => c.deviceId === selectedCamera);
        const nextIndex = (currentIndex + 1) % availableCameras.length;
        setSelectedCamera(availableCameras[nextIndex].deviceId);
      }

      const constraints = getMediaConstraints();
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStreamRef.current = stream;
      setActualFacingMode(detectFacing(stream));
      if (videoRef.current) videoRef.current.srcObject = stream;

      if (peerConnectionRef.current) {
        const senders = peerConnectionRef.current.getSenders();
        const newTracks = stream.getTracks();
        senders.forEach(sender => {
          const newTrack = newTracks.find(t => t.kind === sender.track.kind);
          if (newTrack) sender.replaceTrack(newTrack);
        });
      }
    } catch (err) {
      console.error('❌ Failed to switch camera:', err);
      setError('Failed to switch camera.');
    }
  };

  const stopStreaming = () => {
    const resourceUrl = resourceUrlRef.current;
    const authHeader = credsRef.current?.authHeader;
    resourceUrlRef.current = null;
    credsRef.current = null;
    if (resourceUrl) {
      fetch(resourceUrl, { method: 'DELETE', headers: authHeader ? { 'Authorization': authHeader } : {} }).catch(() => {});
    }
    if (peerConnectionRef.current) { peerConnectionRef.current.close(); peerConnectionRef.current = null; }
    if (localStreamRef.current) { localStreamRef.current.getTracks().forEach(t => t.stop()); localStreamRef.current = null; }
    if (videoRef.current) videoRef.current.srcObject = null;

    setIsStreaming(false);
    setStreamUrl('');
  };

  const copyStreamUrl = () => {
    navigator.clipboard.writeText(streamUrl);
  };

  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  if (!visible) return null;

  return (
    <div className="livestream-overlay">
      <div className="livestream-container">
        <div className="livestream-header">
          <h2>
            Go Live
            {isStreaming && <span className="live-badge live-badge-dot">🔴 LIVE</span>}
          </h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="livestream-body">
          <div className="video-preview">
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className="preview-video"
              style={{ transform: isMobile && actualFacingMode === 'user' ? 'scaleX(-1)' : 'none' }}
            />
            {!isStreaming && !localStreamRef.current && (
              <div className="preview-placeholder">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 7l-7 5 7 5V7z"></path>
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                </svg>
                <p>Camera Preview</p>
              </div>
            )}

            {/* Camera switch (اختيار الكاميرا) */}
            {isStreaming && isMobile && (
              <button
                onClick={switchCamera}
                title="Switch Camera"
                style={{
                  position: 'absolute', top: '10px', right: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.6)', color: 'white', border: 'none',
                  borderRadius: '50%', width: '40px', height: '40px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', zIndex: 10
                }}
              >
                🔄
              </button>
            )}
          </div>

          {/* Camera picker — desktop only, pre-stream (mobile uses the 🔄 switch button above) */}
          {!isMobile && !isStreaming && availableCameras.length > 1 && (
            <div style={{ marginTop: '10px' }}>
              <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Camera:</label>
              <select
                value={selectedCamera}
                onChange={(e) => setSelectedCamera(e.target.value)}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' }}
              >
                {availableCameras.map((camera, index) => (
                  <option key={camera.deviceId} value={camera.deviceId}>
                    {camera.label || `Camera ${index + 1}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Neutral reminder — match your phone's orientation to the display this will show on, landscape or portrait */}
          {isMobile && !isStreaming && (
            <div className="orientation-hint" style={{
              marginTop: '10px', padding: '10px 14px', borderRadius: '8px',
              background: 'rgba(255,193,7,0.15)', border: '1px solid #ffc107', color: '#ffc107', fontSize: '13px'
            }}>
              📱 Hold your phone in the same orientation as the screen this will display on (landscape or portrait).
            </div>
          )}

          <div className="livestream-controls">
            {!isStreaming ? (
              <button
                className="btn-start"
                onClick={startStreaming}
                disabled={connecting}
                style={{ opacity: connecting ? 0.6 : 1, cursor: connecting ? 'not-allowed' : 'pointer' }}
              >
                {connecting ? 'Connecting...' : 'Start Streaming'}
              </button>
            ) : (
              <>
                <button className="btn-stop" onClick={stopStreaming}>Stop Streaming</button>
                <div className="stream-info">
                  <div className="url-section">
                    <label>Stream link (copy &amp; paste anywhere):</label>
                    <div className="url-copy">
                      <input type="text" value={streamUrl} readOnly />
                      <button onClick={copyStreamUrl}>Copy</button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveStream;
